import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import i18n from '@/i18n'
import store from '@/store'
import { AUTH__RESET } from '@/store/actions'
import { localStorageGetMap } from '@/helpers/localStorage'

export type ApiError = AxiosError<{
  status: number
  code: number
  message: string
}>

class ApiService {
  private static _instance: ApiService
  private baseURL = process.env.VUE_APP_API_URL || '/api/'
  private axiosInstance: AxiosInstance = axios.create({
    baseURL: this.baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  private constructor() {
    this.axiosInstance.interceptors.request.use(config => {
      const { token, company } = localStorageGetMap('token', 'company')
      if (typeof token === 'string' && token.length > 0) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
      if (company !== '') {
        config.headers['X-Company-Id'] = company
      }

      return config
    })

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<unknown>): AxiosResponse<unknown> => {
        return response
      },
      (error: AxiosError<ApiError>): Promise<ApiError> => {
        const status = error.response ? error.response.status : null

        if (status === 401) {
          store.dispatch(AUTH__RESET)
          store.dispatch(ADD_TOAST_MESSAGE, {
            text: i18n.tc('auth.needAuthError'),
            type: 'warning',
          })
          return Promise.reject('')
        }

        if (status === 400 || status === 404) {
          store.dispatch(ADD_TOAST_MESSAGE, {
            text: i18n.tc(
              `serverErrors.${error.response?.data.code || 'unknownError'}`,
            ),
            type: 'danger',
          })
          return Promise.reject('')
        }

        if (status === 403) {
          store.dispatch(ADD_TOAST_MESSAGE, {
            text: 'Ошибка уровня доступа',
            type: 'danger',
          })
          return Promise.reject('')
        }

        // TODO: Remove after debugging
        // eslint-disable-next-line
        console.error('Error interceptor')
        // eslint-disable-next-line
        console.error(error.response?.data)
        return Promise.reject(error.response?.data)
      },
    )
  }

  static createInstance(): void {
    ApiService.getInstance()
  }

  static getInstance(): ApiService {
    return this._instance || (this._instance = new this())
  }

  public get = this.axiosInstance.get
  public post = this.axiosInstance.post
  public put = this.axiosInstance.put
  public delete = this.axiosInstance.delete

  public debug = axios.get
}

export default ApiService.getInstance()
